import * as React from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { Plus } from "lucide-react";

import { cn } from "@/lib/utils";

export type MobileTabItem = {
  key: string;
  label: string;
  /** Navigate here on tap. Omit when {@link MobileTabItem.onSelect} handles the tap. */
  href?: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  matchPaths?: string[];
  /** Tap handler for non-navigation tabs (e.g. the "Menu" sheet trigger). */
  onSelect?: () => void;
  /** Small count bubble on the icon — actionable items only. */
  badge?: number;
};

type MobileTabBarProps = {
  items: MobileTabItem[];
  fabHref?: string;
  className?: string;
};

export function MobileTabBar({ items, fabHref, className }: MobileTabBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const midpoint = Math.ceil(items.length / 2);
  const left = items.slice(0, midpoint);
  const right = items.slice(midpoint);

  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 flex items-center justify-around border-t border-border bg-card/90 px-3.5 pt-2 backdrop-blur-xl md:hidden",
        "pb-[max(env(safe-area-inset-bottom),1.25rem)]",
        className,
      )}
    >
      {left.map((tab) => (
        <TabButton key={tab.key} item={tab} pathname={location.pathname} />
      ))}
      {fabHref ? (
        <button
          type="button"
          onClick={() => navigate(fabHref)}
          aria-label="Tạo mới"
          className="-translate-y-2 inline-flex size-[50px] items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-[0_8px_20px_rgba(79,70,229,0.35),inset_0_1px_0_rgba(255,255,255,0.15)] transition-transform active:scale-95"
        >
          <Plus className="size-[22px]" strokeWidth={2.2} />
        </button>
      ) : null}
      {right.map((tab) => (
        <TabButton key={tab.key} item={tab} pathname={location.pathname} />
      ))}
    </div>
  );
}

function TabButton({ item, pathname }: { item: MobileTabItem; pathname: string }) {
  const matchesExtra = item.matchPaths?.some((p) => pathname.startsWith(p)) ?? false;

  // Non-navigation tab (the "Menu" sheet trigger): plain button, never active.
  if (item.href === undefined) {
    return (
      <button
        type="button"
        onClick={item.onSelect}
        className="flex flex-col items-center gap-1 px-2 py-1.5 text-[11px] font-semibold leading-none text-ink-faint"
      >
        <TabIcon item={item} active={false} />
        <span>{item.label}</span>
      </button>
    );
  }

  return (
    <NavLink
      to={item.href}
      end={item.href === "/"}
      className={({ isActive }) => {
        const active = isActive || matchesExtra;
        return cn(
          "flex flex-col items-center gap-1 px-2 py-1.5 text-[11px] font-semibold leading-none",
          active ? "text-foreground" : "text-ink-faint",
        );
      }}
    >
      {({ isActive }) => {
        const active = isActive || matchesExtra;
        return (
          <>
            <TabIcon item={item} active={active} />
            <span>{item.label}</span>
          </>
        );
      }}
    </NavLink>
  );
}

/** Icon + optional count bubble (top-right corner). */
function TabIcon({ item, active }: { item: MobileTabItem; active: boolean }) {
  return (
    <span className="relative">
      <item.icon
        className={cn("size-5", active ? "text-foreground" : "text-ink-faint")}
        strokeWidth={active ? 2 : 1.6}
      />
      {item.badge !== undefined && item.badge > 0 ? (
        <span className="absolute -right-2 -top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 font-mono text-[11px] font-semibold leading-none text-primary-foreground">
          {item.badge > 9 ? "9+" : item.badge}
        </span>
      ) : null}
    </span>
  );
}
